/**
 * The two halves of an answer, kept per cue (lesson library PRD §4.4). A hold
 * produces the reducer's release (which cue, how fast) and, later, the
 * recorder's clip; they arrive in either order, and the recorder is still
 * draining the last clip when a quick learner presses again. One slot shared
 * by every hold paired the second answer with the first one's audio, so each
 * cue has a slot of its own and the recorder's events go to the oldest hold
 * that is still waiting for one.
 */

export type Release = { cueIndex: number; responseMs: number | null; clip: boolean };

/** What the view sends: `pcm` is null when the cue goes unanswered or the clip is lost. */
export type Ready = { info: Release; pcm: Int16Array | null };

type Slot = { info: Release | null; pcm: Int16Array | 'none' | null };

export class Holds {
  private slots = new Map<number, Slot>();
  /** Holds the recorder has been pressed for and has not yet resolved, oldest first. */
  private recording: number[] = [];

  /**
   * A new hold. `true` when the recorder is to be pressed. While an earlier
   * clip is still draining, a press on the recorder would not start anything
   * (and its eventual clip would be the earlier one's), so this hold is
   * recorded as lost instead: a visible "no answer", not a stuck row.
   */
  begin(cueIndex: number): boolean {
    if (this.recording.length > 0) {
      this.slots.set(cueIndex, { info: null, pcm: 'none' });
      return false;
    }
    this.slots.set(cueIndex, { info: null, pcm: null });
    this.recording.push(cueIndex);
    return true;
  }

  /** The reducer's release for a cue. */
  release(info: Release): Ready | null {
    const slot = this.slots.get(info.cueIndex) ?? { info: null, pcm: null };
    slot.info = info;
    this.slots.set(info.cueIndex, slot);
    return this.take(info.cueIndex);
  }

  /** The recorder's outcome for the oldest hold it is working on: a clip, or `'none'` when none is coming. */
  resolve(pcm: Int16Array | 'none'): Ready | null {
    const cueIndex = this.recording.shift();
    if (cueIndex === undefined) return null;
    const slot = this.slots.get(cueIndex);
    if (!slot) return null;
    slot.pcm = pcm;
    return this.take(cueIndex);
  }

  /** Whether the recorder still owes a result, so a stray one is not taken for the next hold's. */
  get busy(): boolean {
    return this.recording.length > 0;
  }

  private take(cueIndex: number): Ready | null {
    const slot = this.slots.get(cueIndex);
    if (!slot?.info || slot.pcm === null) return null;
    this.slots.delete(cueIndex);
    return { info: slot.info, pcm: slot.info.clip && slot.pcm !== 'none' ? slot.pcm : null };
  }
}
